import { KitElement } from 'shared/kit/kit-element';
import { DEFAULT_COLOR, KitElementState, KitOutfit, makeDefaultElementState } from 'shared/kit/kit-outfit';
import { KitPattern } from 'shared/kit/kit-pattern';
import { KitType } from 'shared/kit/kit-type';
import { TeamKit } from 'shared/kit/team-kit';

/**
 * Комплект выбирается в шаблоне, а не оператором: в данных матча нет сведений о том, в чём
 * команды реально играют. `auto` = команда №1 домашняя, команда №2 гостевая.
 */
export type KitTypeSetting = 'auto' | KitType;

export const AUTO_KIT_TYPE = 'auto';

/** Индекс команды на табло: 0 — первая (домашняя), 1 — вторая (гостевая). */
export type TeamIndex = 0 | 1;

export const resolveKitType = (kitType: KitTypeSetting | undefined, teamIndex: TeamIndex): KitType => {
  if (kitType && kitType !== AUTO_KIT_TYPE) return kitType;
  return teamIndex === 1 ? KitType.AWAY : KitType.HOME;
};

/** Состояния элементов выбранного комплекта. Нет комплекта — пустой набор, дальше сработают дефолты. */
export const resolveKitOutfit = (
  kits: TeamKit[] | undefined,
  kitType: KitTypeSetting | undefined,
  teamIndex: TeamIndex,
): Partial<KitOutfit> => {
  const kit = kits?.find((item) => item.type === resolveKitType(kitType, teamIndex));
  if (!kit) return {};

  return kit.elements.reduce<Partial<KitOutfit>>((outfit, { element, attributes }) => {
    outfit[element] = { pattern: attributes.pattern, colors: attributes.colors ?? [] };
    return outfit;
  }, {});
};

export interface ResolveTeamColorParams {
  teamIndex: TeamIndex;
  kitType?: KitTypeSetting;
  /** Элемент формы, с которого берётся цвет. По умолчанию футболка. */
  element?: KitElement;
  /** 0 — основной цвет, 1 — цвет узора. */
  colorIndex?: number;
}

/**
 * Цвет формы команды для заливки объекта табло. Нет комплекта, элемента или цвета —
 * белый (`DEFAULT_COLOR`): пустая заливка на трансляции хуже, чем нейтральная.
 */
export function resolveTeamColor(kits: TeamKit[] | undefined, params: ResolveTeamColorParams): string {
  const { teamIndex, kitType, element = KitElement.SHIRT, colorIndex = 0 } = params;
  const state: KitElementState = resolveKitOutfit(kits, kitType, teamIndex)[element] ?? makeDefaultElementState();
  return state.colors[colorIndex] || DEFAULT_COLOR;
}

const CONTRAST_DARK = '#000000';
const CONTRAST_LIGHT = '#FFFFFF';

const parseHex = (hex: string): [number, number, number] | null => {
  const value = hex.trim().replace(/^#/, '');
  const full = value.length === 3 ? value.replace(/./g, (char) => char + char) : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [full.slice(0, 2), full.slice(2, 4), full.slice(4, 6)].map((part) => parseInt(part, 16)) as [number, number, number];
};

/** Относительная яркость по WCAG — по ней и решаем, какой текст читается поверх цвета. */
const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
  const channel = (value: number) => {
    const srgb = value / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** Чёрный или белый — тот, что читается поверх цвета формы. Нераспознанный цвет считаем светлым. */
export function resolveContrastColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return CONTRAST_DARK;
  return relativeLuminance(rgb) > 0.5 ? CONTRAST_DARK : CONTRAST_LIGHT;
}

/** Цвета команды для двухцветной плашки: основной и — если комплект действительно двухцветный — второй. */
export interface TeamColorPair {
  primary: string;
  /**
   * Цвет узора. `undefined` у однотонного комплекта: тогда плашка красится одним цветом, а не
   * делится пополам с белой половиной.
   */
  secondary?: string;
}

/**
 * Пара цветов элемента формы. Второй цвет считается настоящим по тому же правилу, что и в превью
 * комплекта (`shared/components/kit/kit-preview.tsx`): узор не `SOLID` и второй цвет задан.
 */
export function resolveTeamColors(kits: TeamKit[] | undefined, params: Omit<ResolveTeamColorParams, 'colorIndex'>): TeamColorPair {
  const { teamIndex, kitType, element = KitElement.SHIRT } = params;
  const state: KitElementState = resolveKitOutfit(kits, kitType, teamIndex)[element] ?? makeDefaultElementState();

  return {
    primary: state.colors[0] || DEFAULT_COLOR,
    secondary: state.pattern !== KitPattern.SOLID && state.colors[1] ? state.colors[1] : undefined,
  };
}
